import { useEffect, useState } from 'react';
import { Button, Dropdown, Table, TableColumnsType, TableProps } from 'antd';
import { connect } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { EllipsisOutlined } from '@ant-design/icons';

import { useLayoutContext } from '../../Layout/context';
import { TicketListColumns,
  TicketListGetParams,
  ticketListGet as ticketListAction,
  ticketListClear as ticketListClearAction } from '../../../store/ticket/list';
import { Ticket, TicketStatus, TicketStatusTitle, useTicket } from '../../../store/ticket';
import ContentHeader from '../../Common/ContentHeader';
import { useSearchParams } from '../../../hooks/useSearchParams';
import { getValidSearchParams } from '../../../utils';
import AnimatedHeight from '../../Common/AnimatedHeight';
import InputSearch from '../../Common/InputSearch';
import Filter from './Filter';
import BadgeTicketStatus from '../../Common/BadgeTicketStatus';
import { displayDate } from '../../../utils/date';
import useTicketActions from './hooks/useTicketActions';

import styles from './index.module.scss';

export const ticketsListDefaultSearchParams: Partial<TicketListGetParams> = {
  page: 1,
  pageSize: 10,
  orderByColumn: 'createdAt',
  orderBy: 'DESC',
  name: '',
  status: null,
  search: '',
  type: '',
  user: '',
  updatedAtStart: '',
  updatedAtEnd: '',
};

interface TicketsProps {
  ticketList: typeof ticketListAction;
  ticketListClear: typeof ticketListClearAction;
}

function Tickets({
  ticketList,
  ticketListClear,
}: TicketsProps): JSX.Element {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayoutContext();

  const { list } = useTicket();
  const {
    contextHolder,
    confirmDelete,
    confirmToggleStatus,
    update,
    deleteTicket,
  } = useTicketActions();

  const { searchParams, replaceSearchParams } = useSearchParams();

  const [isOpenFilter, setIsOpenFilter] = useState(false);

  useEffect(() => {
    setBreadcrumbs([{ title: 'Tickets List' }]);
  }, []);

  useEffect(
    () => () => {
      ticketListClear();
    },
    [],
  );

  useEffect(() => {
    ticketList({
      ...ticketsListDefaultSearchParams,
      ...getValidSearchParams<TicketListGetParams>('*', searchParams),
    });
  }, [searchParams, update.response, deleteTicket.response]);

  const onChange: TableProps<Ticket>['onChange'] = (pagination, _, sorter) => {
    replaceSearchParams({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      orderByColumn: (Array.isArray(sorter) ? sorter[0].field : sorter.field) as TicketListColumns,
      // eslint-disable-next-line no-nested-ternary
      orderBy: (Array.isArray(sorter) ? sorter[0].order : sorter.order)
        ? ((Array.isArray(sorter) ? sorter[0].order : sorter.order) === 'ascend'
          ? 'ASC'
          : 'DESC')
        : '',
    });
  };

  const columns: TableColumnsType<Ticket> = [
    {
      title: 'Ticket No.',
      dataIndex: 'ticketId',
      sorter: true,
      width: 140,
      minWidth: 140,
      fixed: 'left',
      render: (_, { ticketId, id }) => <NavLink to={`/tickets/${id}`}>{ticketId}</NavLink>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      sorter: true,
      width: 200,
      minWidth: 200,
    },
    {
      title: 'Reporter',
      dataIndex: 'user',
      sorter: true,
      minWidth: 200,
      // eslint-disable-next-line max-len
      render: (_: any, record: any) => (record?.user?.email ? <NavLink to={`/users/${record.user.id}`}>{record.user.email}</NavLink> : '-'),
    },
    {
      title: 'Date added',
      dataIndex: 'createdAt',
      sorter: true,
      render: (_, { createdAt }) => displayDate(createdAt),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
      minWidth: 100,
      render: (_, { status }) => <BadgeTicketStatus status={status} />,
    },
    {
      title: 'Actions',
      width: 104,
      dataIndex: 'actions',
      fixed: 'right',
      render: (_, ticket) => (
        <div className={styles.actions}>
          <Dropdown
            menu={{
              items: [
                {
                  key: '0',
                  label: 'View',
                  onClick: () => navigate(`/tickets/${ticket.id}`),
                },
                {
                  key: '2',
                  label: ticket.status === TicketStatus.OPEN
                    ? TicketStatusTitle[TicketStatus.RESOLVED]
                    : TicketStatusTitle[TicketStatus.OPEN],
                  onClick: () => confirmToggleStatus(ticket.id, ticket.status),
                },
                {
                  key: '3',
                  label: 'Delete',
                  onClick: () => confirmDelete(ticket.id),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <ContentHeader title="Tickets List">
        <div className={styles.formContainer}>
          <Button onClick={() => setIsOpenFilter((prevState) => !prevState)}>Filter</Button>
          <InputSearch />
        </div>
      </ContentHeader>
      {contextHolder}

      <AnimatedHeight isOpen={isOpenFilter} className={styles.filterWrapper}>
        <Filter onClose={() => setIsOpenFilter(false)} />
      </AnimatedHeight>

      <Table<Ticket>
        columns={columns}
        rowKey="id"
        loading={list.loading}
        dataSource={list.response?.data || []}
        onChange={onChange}
        pagination={{
          current: +(searchParams.get('page') || 1) || 1,
          pageSize: +(searchParams.get('pageSize') || 10) || 10,
          total: list.response?.meta.itemCount || 0,
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} item${total > 1 ? 's' : ''}`,
        }}
      />
    </>
  );
}

export default connect(() => ({}), {
  ticketList: ticketListAction,
  ticketListClear: ticketListClearAction,
})(Tickets);
